import React from 'react';
import { v4 as uuid } from 'uuid';

import { Checkbox } from '@reckon-web/checkbox';
import { Stack } from '@reckon-web/stack';
import { Text } from '@reckon-web/text';
import { field, useForm, useSubmit, validate } from '@reckon-web/forms';

import { Dialog } from '../components/common/dialog';
import { useUpdateUserFilter } from '../data/useUpdateUserFilter';
import {
  GetUserFilterListResult,
  useGetUserFilterList,
} from '../data/useGetUserFilterList';
import { TextField } from '../components/common/text-field';

import { useDialog } from './use-dialog';

type UserFilterSaveDialogResult = { name: string; isDefault?: boolean };

const UserFilterSaveDialog: React.FC<{
  name: string;
  isDefault?: boolean;
  onClose: (res?: UserFilterSaveDialogResult) => void;
}> = ({ name, isDefault, onClose }) => {
  const schema = field.object({
    name: field.text({
      validate: validate((value) => {
        validate.required(value, 'Filter name ');
        return value;
      }),
    }),
    isDefault: field.boolean(),
  });

  const form = useForm(schema, { name, isDefault });

  const submit = useSubmit(form, (value) => onClose(value));

  return (
    <Dialog
      title="Save filter and layout properties"
      cancelLabel="Cancel"
      onCancel={onClose}
      submitLabel="Save"
      onSubmit={submit}
    >
      <Stack gap="large">
        <TextField label="Filter name" {...form.fields.name.props} />
        <Checkbox
          checked={form.value.isDefault}
          onChange={(value) => {
            form.fields.isDefault.setState({
              ...form.fields.isDefault.state,
              value: value.target.checked,
            });
          }}
        >
          Set as my default filter
        </Checkbox>
      </Stack>
    </Dialog>
  );
};

const UserFilterOverwriteDialog: React.FC<{
  name: string;
  onClose: (ok?: boolean) => void;
}> = ({ name, onClose }) => {
  return (
    <Dialog
      title="Override filter?"
      cancelLabel="Cancel"
      onCancel={onClose}
      submitLabel="Yes, override"
      onSubmit={() => onClose(true)}
    >
      <Text>
        This will replace existing filter saved as{' '}
        <Text inline weight="semibold">
          {name}
        </Text>{' '}
        with current settings.
      </Text>
    </Dialog>
  );
};

export const useSaveUserFilter = () => {
  const { data: userFilters } = useGetUserFilterList();
  const [updateUserFilter] = useUpdateUserFilter(); // TODO: handle loading and error states

  const [Dialog, openDialog] = useDialog();

  const saveUserFilter = React.useCallback(
    async (
      userFilter: Partial<GetUserFilterListResult>,
      onNew?: (cloudId: string) => void
    ) => {
      const dialogResult = await openDialog<UserFilterSaveDialogResult>(
        (props) => (
          <UserFilterSaveDialog
            name={userFilter.name!}
            isDefault={!!userFilter.isDefault}
            {...props}
          />
        ),
        { size: 'small' }
      );
      if (!dialogResult) return;

      if (userFilter.cloudId && dialogResult.name === userFilter.name) {
        return updateUserFilter({
          variables: {
            input: {
              cloudId: userFilter.cloudId,
              name: userFilter.name,
              workView: userFilter.workView!,
              filterDefinition: userFilter.filterDefinition!,
              isDefault: dialogResult.isDefault,
            },
          },
        });
      }

      const replacesUserFilter = userFilters
        ?.filter((filter) => filter.cloudId !== userFilter.cloudId)
        .find((filter) => filter.name === dialogResult.name);

      if (
        replacesUserFilter &&
        !(await openDialog<boolean>(
          (props) => (
            <UserFilterOverwriteDialog name={dialogResult.name} {...props} />
          ),
          { size: 'small' }
        ))
      )
        return;

      const cloudId = uuid();
      const res = updateUserFilter({
        variables: {
          input: {
            cloudId,
            name: dialogResult.name,
            workView: userFilter.workView!,
            filterDefinition: userFilter.filterDefinition!,
            replacesUserFilterId: replacesUserFilter?.cloudId,
            isDefault: dialogResult.isDefault,
          },
        },
      });
      await res;
      onNew?.(cloudId);
      return res;
    },
    [userFilters, updateUserFilter, openDialog]
  );

  return React.useMemo(
    () => [Dialog, saveUserFilter] as [typeof Dialog, typeof saveUserFilter],
    [Dialog, saveUserFilter]
  );
};
